<template>
  <div class="workspace-group-users">
    <title-panel
      :button-label="t('workspaceGroupUsers.addUser')"
      :count="workspaceGroupUsers?.length ?? 0"
      :display-button="canManageWorkspaces"
      :on-button-click="onAddUserButtonClick"
      :title="t('workspaceGroupUsers.users')"
    ></title-panel>

    <user-select-modal
      ref="modalRef"
      modal-id="workspace-group-users-modal"
      :placeholder="t('workspaceGroupDetailView.selectUser')"
      :title="t('workspaceGroupUsers.addUser')"
      :selection-required-message="
        t('workspaceGroupDetailView.userSelectionRequired')
      "
      :ok-button-busy="isAddingWorkspaceParticipant"
      :search-users="getAdGroupMembers"
      @selected-user="handleUserSelected"
    />
  </div>

  <table-wrapper
    v-model:page-options="pageOptions"
    v-model:filters="filters"
    action-icon="delete"
    :display-action-icon="canManageWorkspaces"
    :fields="tableFields"
    :filter-configuration="filterConfiguration"
    :items="workspaceGroupUsers ?? []"
    :loading="isLoading"
    :mode="TableMode.CLIENT_SIDE"
    @action-icon-click="handleDeleteUserClick"
  />
</template>

<script lang="ts" setup>
  import { useRoles } from '@/composables/useRoles';
  import { useWorkspaceGroups } from '@/composables/api/workspaces/useWorkspaceGroups';
  import { IAdUserInternalFormat, IWorkspaceGroupUserDto } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import SpGroupModal from '@/components/SpGroupModal/SpGroupModal.vue';
  import {
    useTableWrapper,
    TableMode,
    TableFieldsBuilder,
    TableFiltersBuilder,
  } from '@/components/TableWrapper';
  import UserSelectModal from '../UserSelectModal/UserSelectModal.vue';
  import endpointsList from '../../helpers/endpointsList';
  import axios, { AxiosResponse } from 'axios';
  import { useToast } from '@/composables/useToast';

  const props = defineProps<{
    workspaceId: string;
    groupId: string;
  }>();

  const { t } = useI18n();
  const { canManageWorkspaces } = useRoles();
  const { createErrorToast } = useToast();
  const isAddingWorkspaceParticipant = ref(false);

  const {
    workspaceGroupUsers,
    fetchWorkspaceGroupUsers,
    removeUserFromWorkspaceGroup,
    addUserToWorkspaceGroup,
  } = useWorkspaceGroups(props.workspaceId);

  const { pageOptions, filters, isLoading, fetch } =
    useTableWrapper<IWorkspaceGroupUserDto>(
      TableMode.CLIENT_SIDE,
      'lastName',
      () => fetchWorkspaceGroupUsers(props.groupId),
    );

  const filterConfiguration = new TableFiltersBuilder<IWorkspaceGroupUserDto>({
    translationKey: 'workspaceGroupUsers',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IWorkspaceGroupUserDto>({
      sortable: true,
      labelTranslationKey: 'workspaceGroupUsers',
    })
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '36%' })
      .addField('email', { width: '34%' })
      .build(),
  );

  async function handleUserSelected(user: IWorkspaceGroupUserDto) {
    await addUserToWorkspaceGroup(props.groupId, user.oid);
    await fetch();
  }

  async function handleDeleteUserClick(user: IWorkspaceGroupUserDto) {
    await removeUserFromWorkspaceGroup(props.groupId, user.oid);
    await fetch();
  }

  const modalRef = ref<InstanceType<typeof SpGroupModal> | null>(null);
  function onAddUserButtonClick(): void {
    modalRef.value?.show();
  }

  async function getAdGroupMembers(
    query: string,
  ): Promise<IAdUserInternalFormat[]> {
    isAddingWorkspaceParticipant.value = true;
    const groupEndpoint =
      endpointsList.workspaces.getUsersFromWorkspacesAdGroup();

    try {
      const response: AxiosResponse<IAdUserInternalFormat[]> = await axios.get(
        groupEndpoint,
        {
          params: { query },
        },
      );
      isAddingWorkspaceParticipant.value = false;
      return response.data;
    } catch {
      createErrorToast(t('workspaceGroupDetailView.errors.couldNotGetUsers'));
      isAddingWorkspaceParticipant.value = false;
      return [];
    }
  }
</script>

<style scoped lang="scss">
  .workspace-group-users {
    margin-top: 2rem;
  }
</style>
